import { NOTE_COLOR_SWATCHES } from '@/app/support/support-markup';

/**
 * Momentvelden-vocabulaire (spec docs/LEVENSTIJDLIJN.md §2.2/§2.3/§2.7):
 * categorieën, stemming (mood), vitaliteit en emoties van een gelogd moment.
 * Frontend-owned — de backend valideert alleen lengtes/bereiken (zelfde recept
 * als de relatietypen).
 */

/**
 * Categorie-keys (beslispunt 8: starten met daily + lifetime). Een gedachte
 * (spec docs/superpowers/specs/2026-10-22-mind-logboek-design.md) is sinds
 * 2026-10-24 een dagelijks moment met een `mind`-blok in `Chart.MomentExtraJson`;
 * de smaak "Gedachte & dagboek" van de momentdialoog zet dat blok.
 */
export const HD_MOMENT_CATEGORIES = ['daily', 'lifetime'] as const;
export type HdMomentCategory = (typeof HD_MOMENT_CATEGORIES)[number];

/**
 * Legacy: de notities van 22 en 23 oktober 2026 kregen een eigen categorie `mind`;
 * ze blijven herkenbaar (logboek, label) maar de categorie is niet meer kiesbaar.
 */
export const HD_MIND_MOMENT_CATEGORY = 'mind';
/** Standaardicoon en -kleur (dusk-preset-key) van een nieuwe mind-notitie; beide aanpasbaar. */
export const HD_MIND_MOMENT_DEFAULT_ICON = 'brain';
export const HD_MIND_MOMENT_DEFAULT_COLOR = 'violet';
/** Categorieën die op de levenstijdlijn standaard verborgen staan (de dagelijkse laag). */
export const HD_MOMENT_CATEGORIES_HIDDEN_ON_TIMELINE: readonly string[] = ['daily', HD_MIND_MOMENT_CATEGORY];

export function hdMomentCategoryLabelKey(category: string): string {
  return `humanDesign.momentFields.category.${category}`;
}

// ── Stemming (mood) ─────────────────────────────────────────────────────────

/**
 * Vaste 5-puntsschaal (finetune 2026-09-22, verving de 7-schaal met
 * 5/7-weergave-instelling): 1 = fijnst (groen), 3 = neutraal (amber),
 * 5 = zwaarst (rood).
 */
export const HD_MOOD_VALUES = [1, 2, 3, 4, 5] as const;

/**
 * Evenredige 5-staps gradiënt groen → amber → rood in dusk-tinten (uiteinden =
 * green/red van het notitiepalet, midden = amber; 2 en 4 zijn de exacte
 * tussenkleuren).
 */
export const HD_MOOD_COLORS: Readonly<Record<number, string>> = {
  1: '#4f8a5c',
  2: '#929a63',
  3: '#d4a869',
  4: '#be7e5c',
  5: '#a85550',
};

export function hdMoodColor(mood: number | null | undefined): string | null {
  return mood != null ? HD_MOOD_COLORS[mood] ?? null : null;
}

export function hdMoodLabelKey(mood: number): string {
  return `humanDesign.momentFields.mood.${mood}`;
}

// ── Vitaliteit ──────────────────────────────────────────────────────────────

/** Energiepeil 1..5, batterij-metafoor (1 = leeg, 5 = vol). */
export const HD_VITALITY_VALUES = [1, 2, 3, 4, 5] as const;

export const HD_VITALITY_ICONS: Readonly<Record<number, string>> = {
  1: 'battery-empty',
  2: 'battery-quarter',
  3: 'battery-half',
  4: 'battery-three-quarters',
  5: 'battery-full',
};

export function hdVitalityLabelKey(vitality: number): string {
  return `humanDesign.momentFields.vitality.${vitality}`;
}

// ── Emoties ─────────────────────────────────────────────────────────────────

export interface HdMomentEmotion {
  readonly key: string;
  readonly valence: 'positive' | 'negative';
  /** Kale FA Pro-naam, getoond in de chip (geverifieerd tegen fa-icon-index). */
  readonly icon: string;
}

/**
 * Gecureerde emotielijst (finetune 2026-09-21): eerst de acht
 * HD-signpost-woorden als paren naast elkaar (signature ↔ not-self per type:
 * voldaan/gefrustreerd, succesvol/bitter, vredig/boos, verrast/teleurgesteld),
 * daarna de bredere selectie. Opslag = komma-gescheiden keys op
 * `Chart.Emotions`.
 */
export const HD_MOMENT_EMOTIONS: readonly HdMomentEmotion[] = [
  // Signpost-paren (signature ↔ not-self), in vaste volgorde bij elkaar.
  { key: 'satisfied', valence: 'positive', icon: 'face-smile-relaxed' },
  { key: 'frustrated', valence: 'negative', icon: 'face-confounded' },
  { key: 'successful', valence: 'positive', icon: 'trophy' },
  { key: 'bitter', valence: 'negative', icon: 'face-rolling-eyes' },
  { key: 'peaceful', valence: 'positive', icon: 'dove' },
  { key: 'angry', valence: 'negative', icon: 'face-angry' },
  { key: 'surprised', valence: 'positive', icon: 'face-surprise' },
  { key: 'disappointed', valence: 'negative', icon: 'face-disappointed' },
  // Bredere lijst.
  { key: 'happy', valence: 'positive', icon: 'face-laugh-beam' },
  { key: 'grateful', valence: 'positive', icon: 'hands-praying' },
  { key: 'excited', valence: 'positive', icon: 'face-grin-stars' },
  { key: 'proud', valence: 'positive', icon: 'award' },
  { key: 'relaxed', valence: 'positive', icon: 'spa' },
  { key: 'calm', valence: 'positive', icon: 'face-relieved' },
  { key: 'curious', valence: 'positive', icon: 'magnifying-glass' },
  { key: 'anxious', valence: 'negative', icon: 'face-anxious-sweat' },
  { key: 'sad', valence: 'negative', icon: 'face-sad-tear' },
  { key: 'lonely', valence: 'negative', icon: 'face-pensive' },
  { key: 'stressed', valence: 'negative', icon: 'gauge-high' },
  { key: 'bored', valence: 'negative', icon: 'face-meh' },
];

/** Maximaal aantal emoties per moment (chips-invoer). */
export const HD_MOMENT_MAX_EMOTIONS = 3;

/**
 * De vier signature ↔ not-self-paren sluiten elkaar uit (finetune 2026-09-21):
 * je bent voldaan óf gefrustreerd, niet allebei tegelijk.
 */
export const HD_SIGNPOST_PAIRS: readonly (readonly [string, string])[] = [
  ['satisfied', 'frustrated'],
  ['successful', 'bitter'],
  ['peaceful', 'angry'],
  ['surprised', 'disappointed'],
];

/** De uitsluitende tegenhanger binnen een signpost-paar; null voor vrije emoties. */
export function hdSignpostCounterpart(key: string): string | null {
  for (const [positive, negative] of HD_SIGNPOST_PAIRS) {
    if (key === positive) return negative;
    if (key === negative) return positive;
  }
  return null;
}

/**
 * Chip-toggle met de signpost-regel: aanklikken van een tegenhanger vervangt
 * hem (voldaan → gefrustreerd wisselt), vrije emoties combineren tot het
 * maximum; nogmaals klikken zet uit.
 */
export function toggleHdMomentEmotion(current: readonly string[], key: string): string[] {
  if (current.includes(key)) return current.filter(existing => existing !== key);
  const counterpart = hdSignpostCounterpart(key);
  const base = counterpart != null ? current.filter(existing => existing !== counterpart) : [...current];
  if (base.length >= HD_MOMENT_MAX_EMOTIONS) return [...current];
  return [...base, key];
}

export function hdEmotionLabelKey(key: string): string {
  return `humanDesign.momentFields.emotions.${key}`;
}

/**
 * Signpost-paar (signature ↔ not-self) per HD-type, summary-vocabulaire
 * ('generator', 'manifestingGenerator', …). De invoer licht dit paar bovenaan
 * uit: één tik legt het signature-/not-self-signaal van het moment vast.
 */
export function hdSignpostPairForType(hdType: string | null | undefined): { positive: string; negative: string } | null {
  switch (hdType) {
    case 'generator':
    case 'manifestingGenerator':
      return { positive: 'satisfied', negative: 'frustrated' };
    case 'manifestor':
      return { positive: 'peaceful', negative: 'angry' };
    case 'projector':
      return { positive: 'successful', negative: 'bitter' };
    case 'reflector':
      return { positive: 'surprised', negative: 'disappointed' };
    default:
      return null;
  }
}

/** Komma-opslagvorm ↔ keys-array (onbekende/lege stukken vallen weg). */
export function parseHdMomentEmotions(emotions: string | null | undefined): string[] {
  if (!emotions) return [];
  const known = new Set(HD_MOMENT_EMOTIONS.map(emotion => emotion.key));
  return emotions
    .split(',')
    .map(part => part.trim())
    .filter(part => known.has(part));
}

export function joinHdMomentEmotions(keys: readonly string[]): string | null {
  return keys.length ? keys.join(',') : null;
}

// ── Kleur ───────────────────────────────────────────────────────────────────

/**
 * Momentkleur-waarde (`Chart.Color`): een dusk-preset-key uit het
 * notitiepalet, of een custom "#rrggbb". Deze helper lost beide op naar hex
 * voor weergave; onbekende keys geven null (geen kleur).
 */
export function hdMomentColorHex(color: string | null | undefined): string | null {
  if (!color) return null;
  if (color.startsWith('#')) return color;
  return NOTE_COLOR_SWATCHES.find(swatch => swatch.key === color)?.hex ?? null;
}
